import { useState, useEffect } from 'react';
import { API } from '@/lib/api';
import { toast } from 'sonner';
import { Trophy, Medal, Award, TrendingUp } from 'lucide-react';
import Navigation from '@/components/Navigation';

function Leaderboard() {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLeaderboard();
  }, []);

  const loadLeaderboard = async () => {
    try {
      const response = await API.get('/leaderboard');
      setLeaderboard(response.data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load leaderboard');
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy className="text-yellow-500 w-8 h-8" />;
    if (rank === 2) return <Medal className="text-gray-400 w-8 h-8" />;
    if (rank === 3) return <Award className="text-orange-600 w-8 h-8" />;
    return <span className="text-gray-500 font-bold text-xl">#{rank}</span>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navigation />
      
      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <Trophy className="text-yellow-500 w-16 h-16 mx-auto mb-4" />
          <h1 className="font-['Outfit'] text-4xl font-bold mb-3" data-testid="leaderboard-title">Top Ninjas</h1>
          <p className="text-gray-400">This month's most successful negotiators</p>
        </div>

        {leaderboard.length === 0 ? (
          <div className="ninja-card text-center py-12">
            <TrendingUp className="text-gray-600 w-16 h-16 mx-auto mb-4" />
            <p className="text-gray-400">No wins logged yet. Be the first!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {leaderboard.map((entry, idx) => (
              <div 
                key={idx} 
                className={`ninja-card flex items-center gap-6 ${idx < 3 ? 'border-yellow-500/30' : ''}`}
                data-testid={`leaderboard-rank-${entry.rank}`}
              >
                <div className="flex-shrink-0 w-16 flex justify-center">
                  {getRankIcon(entry.rank)}
                </div>
                <div className="flex-1">
                  <div className="font-['Outfit'] text-xl font-bold mb-1">
                    {entry.user_name}
                  </div>
                  <div className="text-sm text-gray-400">
                    {entry.win_count} {entry.win_count === 1 ? 'win' : 'wins'}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-green-500" data-testid={`savings-${entry.rank}`}>
                    £{entry.total_saved.toFixed(0)}
                  </div>
                  <div className="text-sm text-gray-400">saved</div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-8 ninja-card bg-gradient-to-r from-green-500/10 to-transparent border-green-500/30 text-center">
          <p className="text-gray-300 mb-2">🔥 Keep logging your wins to climb the ranks!</p>
          <p className="text-sm text-gray-500">Leaderboard resets monthly</p>
        </div>
      </div>
    </div>
  );
}

export default Leaderboard;